import React, { useState } from 'react';
import { CheckCircle2, X, Settings } from 'lucide-react';
import AutoavaliacaoConsulta from './AutoavaliacaoConsulta';
import AutoavaliacaoQuestionario from './AutoavaliacaoQuestionario';
import AutoavaliacaoDetalhe from './AutoavaliacaoDetalhe';
import AutoavaliacaoVersoes from './AutoavaliacaoVersoes';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// =====================================================================
// Sub-hub "Autoavaliação de Controles" (Módulo Operacional).
//
// No padrão do OperacionalBsv: a tela inicial é a CONSULTA, e as ações vivem no
// cabeçalho dela. A navegação para fora é a barra de abas do módulo — por isso
// a consulta não tem "Voltar" próprio.
//
// ⚠️ QUEM PREENCHE É O GERENTE — o inverso do BSV, onde o Gerente só lê. Aqui a
//    autoavaliação é da loja dele. O `Operacional` acompanha a rede sem
//    responder; a `Diretoria` (e o `Diretoria Op`, que herda dela) também
//    preenche desde 2026-09-03, escolhendo a loja na tela como o Admin.
//    Estes gates são só a TELA: quem de fato segura são as duas listas de perfil
//    do server-autoavaliacao.js.
//
// A paleta é a ardósia do módulo Operacional (#475569). As cores das faixas de
// risco (verde/amarelo/laranja/vermelho) são semânticas — dizem o que o número
// é, não onde o usuário está (docs/ARQUITETURA.md › Paleta dos módulos).
// =====================================================================

const OperacionalAutoavaliacao = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const perfil = perfilNoModulo(user?.perfil, 'operacional') || '';
  // ⚠️ ESPELHO do PERFIS_PREENCHE do server-autoavaliacao.js — divergir não dá
  // erro em lugar nenhum: ou o botão some para quem podia usar, ou ele aparece e
  // a chamada volta 403. Há teste travando os dois lados.
  // (`Diretoria` entrou em 2026-09-03; ela e o `Diretoria Op` escolhem a loja na
  // tela, como o Admin, porque são do ESCRITÓRIO e não têm loja no cadastro.)
  const podePreencher = ['Gerente', 'Diretoria', 'Admin'].includes(perfil);
  // ⚠️ ESPELHO do PERFIS_GESTAO do server-autoavaliacao-versoes.js — quem
  // preenche (Gerente/Diretoria) não mexe na estrutura do formulário.
  const podeGerenciarVersoes = ['Admin', 'Operacional'].includes(perfil);

  const [tela, setTela] = useState('lista'); // lista | preencher | detalhe | versoes
  const [idAberto, setIdAberto] = useState(null);
  const [sucesso, setSucesso] = useState(null);
  // Muda a cada gravação/cancelamento para a consulta se recarregar sozinha.
  const [versaoLista, setVersaoLista] = useState(0);

  const voltarParaLista = () => {
    setTela('lista');
    setIdAberto(null);
  };

  const gravou = (resultado) => {
    setSucesso(resultado);
    setVersaoLista((v) => v + 1);
    voltarParaLista();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (tela === 'preencher') {
    return (
      <AutoavaliacaoQuestionario
        onVoltar={voltarParaLista}
        onGravou={gravou}
      />
    );
  }

  if (tela === 'detalhe' && idAberto) {
    return (
      <AutoavaliacaoDetalhe
        id={idAberto}
        podeCancelar={podePreencher}
        onVoltar={voltarParaLista}
        onCancelou={() => { setVersaoLista((v) => v + 1); voltarParaLista(); }}
      />
    );
  }

  if (tela === 'versoes') {
    return <AutoavaliacaoVersoes onVoltar={voltarParaLista} />;
  }

  // "Gerenciar versões" mora DENTRO do cabeçalho da consulta (via acoesExtras),
  // no mesmo padrão da Avaliação 360 — não numa barra separada acima. O estilo
  // espelha o btnSecundario da consulta para os botões ficarem idênticos.
  const acoesVersoes = podeGerenciarVersoes ? (
    <button type="button" onClick={() => { setSucesso(null); setTela('versoes'); }} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
      background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8,
      fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit',
    }}>
      <Settings size={16} /> Gerenciar versões
    </button>
  ) : null;

  return (
    <div>
      {/* Confirmação da gravação — some ao ser fechada ou ao trocar de tela. */}
      {sucesso && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10,
          padding: '14px 16px', background: '#d1fae5', border: '1px solid #6ee7b7',
          borderRadius: 10, marginBottom: 18, color: '#065f46',
        }}>
          <CheckCircle2 size={20} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, fontSize: 13.5, lineHeight: 1.6 }}>
            <strong>Autoavaliação gravada.</strong> {sucesso.loja} · {sucesso.anomes} —{' '}
            {sucesso.qtdNao} “Não” de {sucesso.qtdAplicavel} aplicáveis
            {' '}({Number(sucesso.percentualRisco).toFixed(2)}%).
          </div>
          <button type="button" onClick={() => setSucesso(null)} style={{
            border: 'none', background: 'transparent', color: '#065f46',
            cursor: 'pointer', padding: 2,
          }}>
            <X size={16} />
          </button>
        </div>
      )}

      <AutoavaliacaoConsulta
        podePreencher={podePreencher}
        recarregarEm={versaoLista}
        acoesExtras={acoesVersoes}
        onNova={() => { setSucesso(null); setTela('preencher'); }}
        onAbrir={(id) => { setIdAberto(id); setTela('detalhe'); }}
      />
    </div>
  );
};

export default OperacionalAutoavaliacao;
